import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';
import {
  Button,
  DeleteModal,
  ExpandableTable,
  ICONS,
  PLATFORM_THEME_COLORS,
  SearchInput,
  Typography,
  TYPOGRAPHY_SIZE,
  HolidayGroupTable,
  ButtonsWrapper,
  EmptyContainer,
  EMPTY_ILLUSTRATIONS,
} from '@thinknet-gmbh/platform-common-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import PermissionCheck from 'src/components/permission-check';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useDeleteHolidaySubGroup } from 'src/hooks/holiday-manager/delete-holiday-subgroup.hook';
import { useGetHolidayLocationsByCompany } from 'src/hooks/holiday-manager/get-holiday-locations-by-company.hook';
import { usePermissionCheck } from 'src/hooks/permission-check.hook';

import { CreateHolidayGroup } from './components/create-holiday-group';
import { CreateLocation } from './components/create-location';
import { UseExistedHolidayGroup } from './components/use-existed-holiday-group';
import {
  HOLIDAY_MANAGER_MODALS,
  HOLIDAY_MANAGER_PAGES,
  TargetIds,
} from './holiday-manager.constants';
import { useHolidayNavigate } from './holiday-manager.hook';
import { HeaderContainer, MainContainer } from './holiday-manager.styles';

const HolidayManager = () => {
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [targetIds, setTargetIds] = useState<TargetIds>();
  const { checkRule } = usePermissionCheck(PLATFORM_PERMISSIONS.holidayManagement);

  const { pageNavigate } = useHolidayNavigate();
  const { t } = useTranslation(['common', 'settings']);

  const currentCompanyId = useGetCurrentCompanyId();
  const { holidayLocations, paginationConfig } = useGetHolidayLocationsByCompany(
    currentCompanyId,
    searchValue
  );

  const deleteHolidaySubGroup = useDeleteHolidaySubGroup();

  const modalHandler = (modal: string, ids: TargetIds) => {
    setActiveModal(modal);
    setTargetIds(ids);
  };

  const onSubmitDeleteSubGroup = () => {
    deleteHolidaySubGroup({ variables: { holidaySubGroupId: targetIds?.holidaySubGroupId } });
    setActiveModal(null);
  };

  return (
    <MainContainer>
      {activeModal === HOLIDAY_MANAGER_MODALS.deleteGroup && (
        <DeleteModal
          isOpened
          onClose={() => setActiveModal(null)}
          onSubmit={onSubmitDeleteSubGroup}
          title={t('settings:holidayManager.deleteGroup.title')}
          subTitle={t('settings:holidayManager.deleteGroup.subtitle')}
          translate={t}
        />
      )}
      {activeModal === HOLIDAY_MANAGER_MODALS.createGroup && (
        <CreateHolidayGroup
          onClose={() => setActiveModal(null)}
          initialHolidayLocation={targetIds?.holidayLocationId}
        />
      )}
      {activeModal === HOLIDAY_MANAGER_MODALS.useExistedGroup && (
        <UseExistedHolidayGroup onClose={() => setActiveModal(null)} />
      )}
      {activeModal === HOLIDAY_MANAGER_MODALS.createLocation && (
        <CreateLocation onClose={() => setActiveModal(null)} />
      )}
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('settings:holidayManager.labels.holidayManager')}
        </Typography>
        <div>
          <SearchInput value={searchValue} onChange={e => setSearchValue(e.target.value)} />
          <PermissionCheck
            permission={PLATFORM_PERMISSIONS.holidayManagement}
            action={PERMISSION_ACTIONS.create}
          >
            <ButtonsWrapper>
              <Button
                type="primary"
                ghost
                icon={ICONS.plus}
                iconColor={PLATFORM_THEME_COLORS.primary.blue}
                onClick={() => setActiveModal(HOLIDAY_MANAGER_MODALS.createLocation)}
              >
                {t('settings:holidayManager.buttons.createANewLocation')}
              </Button>
              <Button
                type="primary"
                icon={ICONS.listPlus}
                iconColor={PLATFORM_THEME_COLORS.primary.white}
                onClick={() => setActiveModal(HOLIDAY_MANAGER_MODALS.useExistedGroup)}
              >
                {t('settings:holidayManager.buttons.useExistedGroup')}
              </Button>
            </ButtonsWrapper>
          </PermissionCheck>
        </div>
      </HeaderContainer>
      {holidayLocations.length > 0 ? (
        <ExpandableTable
          t={t}
          dataSource={holidayLocations}
          isRowClicked
          paginationConfig={paginationConfig}
          expandedRowRender={(holidayLocation: any) => {
            const canEdit = checkRule(
              PERMISSION_ACTIONS.update,
              accountId => accountId === holidayLocation.createdBy
            );
            const canDelete = checkRule(
              PERMISSION_ACTIONS.delete,
              accountId => accountId === holidayLocation.createdBy
            );
            const canCreate = checkRule(PERMISSION_ACTIONS.create);

            return (
              <HolidayGroupTable
                dataSource={holidayLocation.holidaySubGroups}
                deleteGroup={modalHandler}
                onRowClick={id => pageNavigate(HOLIDAY_MANAGER_PAGES.details, id)}
                editGroup={id => pageNavigate(HOLIDAY_MANAGER_PAGES.edit, id)}
                columnName={t('settings:holidayManager.labels.holidayGroup')}
                canCreate={!canCreate}
                canDelete={!canDelete}
                canEdit={!canEdit}
                onClickFooterBtn={() =>
                  modalHandler(HOLIDAY_MANAGER_MODALS.createGroup, {
                    holidayLocationId: holidayLocation._id,
                  })
                }
                footerButtonTxt={t('settings:holidayManager.buttons.createNewHolidayGroup')}
                t={t}
              />
            );
          }}
        >
          <ExpandableTable.Column
            title={t('common:labels.location')}
            render={record => (
              <Typography
                onClick={() => {
                  if (
                    checkRule(
                      PERMISSION_ACTIONS.update,
                      accountId => accountId === record.createdBy
                    )
                  ) {
                    pageNavigate(HOLIDAY_MANAGER_PAGES.locationEdit, record._id);
                  }
                }}
              >
                {record.name}
              </Typography>
            )}
          />
        </ExpandableTable>
      ) : (
        <EmptyContainer
          illustration={EMPTY_ILLUSTRATIONS.dataAnalysis}
          emptyTitle={t('settings:holidayManager.emptyContainer.title')}
          contentText={t('settings:holidayManager.emptyContainer.subtitle')}
        />
      )}
    </MainContainer>
  );
};

export default HolidayManager;
